import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { toast } from 'react-toastify';
import { Document, Director, DocumentType } from '../types/dataRoom';
import { createDocument } from '../utils/documentUtils';
import { v4 as uuidv4 } from 'uuid';
import apiService from '../services/apiService';
import { useParams } from "react-router-dom";

interface ApiResponse<T> {
  documents: T;
  message?: string;
  status?: number;
  // financial?:FinancialTabProps
}

interface DataRoomContextType {
  documents: Document[];
  directors: Director[];
  addDocument: (file: File, type: DocumentType, directorId?: string, tags?: string[]) => void;
  addDocuments: () => void;
  removeDocument: (documentId: string) => void;
  toggleDocumentSelection: (documentId: string) => void;
  selectAllDocuments: (selected: boolean) => void;
  addDirector: (name: string) => void;
  editDirector: (id: string, name: string) => void;
  removeDirector: (directorId: string) => void;
  selectedDocumentsCount: number;
  getSelectedDocuments: () => Document[];
  updateDocumentTags: (documentId: string, tags: string[]) => void;
  updateDocumentType: (documentId: string, type: DocumentType) => void;
}

const DataRoomContext = createContext<DataRoomContextType | undefined>(undefined);

export const DataRoomProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [directors, setDirectors] = useState<Director[]>([]);
  const [selectedDocumentsCount, setSelectedDocumentsCount] = useState(0);
  const { customerId } = useParams();
  useEffect(() => {
    // Update selected documents count whenever documents change
    const count = documents.filter(doc => doc.isSelected).length;
    setSelectedDocumentsCount(count);
  }, [documents]);



  

  const addDocument = (file: File, type: DocumentType, directorId?: string, tags?: string[]) => {
 
    
    const newDocument = createDocument(file, type, directorId, tags);
 
    
    setDocuments(prev => [...prev, newDocument]);
    toast.success(`Document "${file.name}" added successfully`);
  };

  const addDocuments = () => {
    getDocuments();
    toast.success(`documents added successfully`);
  };

  const removeDocument =async (documentId: string) => {
    
    const [response] = await Promise.all([
      apiService.post<ApiResponse<any>>(`/data-room/delete`,{recordId : documentId})
    ]);
    if(response.status == 200){
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));
      toast.info('Document removed');

    }else{
      toast.success('Failed');

    }


  
  };

  const toggleDocumentSelection = (documentId: string) => {
    setDocuments(prev => prev.map(doc => 
      doc.id === documentId ? { ...doc, isSelected: !doc.isSelected } : doc
    ));
  };

  const selectAllDocuments = (selected: boolean) => {
    setDocuments(prev => prev.map(doc => ({ ...doc, isSelected: selected })));
  };

  const getSelectedDocuments = () => {
    return documents.filter(doc => doc.isSelected);
  };

  const addDirector = (name: string) => {
    const newDirector: Director = {
      id: uuidv4(),
      name,
      documents: []
    };
    setDirectors(prev => [...prev, newDirector]);
    toast.success(`Director "${name}" added successfully`);
  };

  const editDirector = (id: string, name: string) => {
    setDirectors(prev => prev.map(director => 
      director.id === id ? { ...director, name } : director
    ));
    toast.success('Director updated');
  };

  const removeDirector = (directorId: string) => {
    // Remove director
    setDirectors(prev => prev.filter(director => director.id !== directorId));
    
    // Remove all documents associated with this director
    setDocuments(prev => prev.filter(doc => doc.directorId !== directorId));
    
    toast.info('Director and associated documents removed');
  };

  const updateDocumentTags = (documentId: string, tags: string[]) => {
    setDocuments(prev => prev.map(doc => 
      doc.id === documentId ? { ...doc, tags } : doc
    ));
    toast.success('Document tags updated');
  };

  const updateDocumentType = async (documentId: string, type: DocumentType) => {
    const cat = getDocumentCategory(type);

    const [response] = await Promise.all([
      apiService.post<ApiResponse<any>>(`/data-room/update-status/${documentId}`,{cat,type})
    ]);
    if(response.status == 200){
      setDocuments(prev => prev.map(doc => 
        doc.id === documentId ? { ...doc, type } : doc
      ));
      toast.success('Document type updated');

    }else{
      toast.success('Failed');

    }

   
  };

  const getDocumentCategory = (type: DocumentType): string => {
    
    switch(type) {
      case 'audited_financials_2022_23':
      case 'audited_financials_2023_24':
      case 'audited_financials_2024_25':
      case 'provisional_financials':
      case 'mis_financials':
      case 'itr':
        return 'Financials';
      case 'bank_statements':
      case 'gst_filing':
      case 'sanction_letter':
        return 'Banking';
      case 'company_kyc':
      case 'director_kyc':
      case 'company_deck':
        return 'KYC Documents';
      default:
        return 'Other';
    }
  };


  const getDocuments = async()=>{
  
    const [response] = await Promise.all([
      apiService.get<ApiResponse<any>>(`/data-room/documents/${customerId}`)
    ]);
    setDocuments(response?.documents);
  }

  useEffect(() => {
    if (customerId) {
      getDocuments();
    }
  }, [customerId]);

  const value = {
    documents,
    directors,
    addDocument,
    addDocuments,
    removeDocument,
    toggleDocumentSelection,
    selectAllDocuments,
    addDirector,
    editDirector,
    removeDirector,
    selectedDocumentsCount,
    getSelectedDocuments,
    updateDocumentTags,
    updateDocumentType,
  };

  return <DataRoomContext.Provider value={value}>{children}</DataRoomContext.Provider>;
};



export const useDataRoom = () => {
  const context = useContext(DataRoomContext);
  if (context === undefined) {
    throw new Error('useDataRoom must be used within a DataRoomProvider');
  }
  return context;
};
